// SPDX-License-Identifier: AGPL-3.0-or-later

import {Routes} from '@app/app/Routes';
import * as Modal from '@app/features/app/components/dialogs/Modal';
import {CopyLinkSection} from '@app/features/app/components/dialogs/shared/CopyLinkSection';
import type {RecipientItem} from '@app/features/app/components/dialogs/shared/RecipientList';
import {RecipientList, useRecipientItems} from '@app/features/app/components/dialogs/shared/RecipientList';
import selectorStyles from '@app/features/app/components/dialogs/shared/SelectorModalStyles.module.css';
import {Endpoints} from '@app/features/app/constants/Endpoints';
import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import * as PrivateChannelCommands from '@app/features/channel/commands/PrivateChannelCommands';
import * as MessageCommands from '@app/features/messaging/commands/MessageCommands';
import {conduit} from '@app/features/platform/transport/RestTransport';
import {Logger} from '@app/features/platform/utils/AppLogger';
import styles from '@app/features/theme/components/modals/ShareThemeModal.module.css';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Input} from '@app/features/ui/components/form/Input';
import {Spinner} from '@app/features/ui/components/Spinner';
import {useCopyLinkHandler} from '@app/lib/copy-link';
import * as SnowflakeUtils from '@app/vendor/snowflake/SnowflakeUtils';
import {Trans, useLingui} from '@lingui/react/macro';
import {MagnifyingGlassIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import {useEffect, useState} from 'react';

const logger = new Logger('ShareThemeModal');
export const ShareThemeModal = observer(({themeCss}: {themeCss: string}) => {
	const {t} = useLingui();
	const [themeUrl, setThemeUrl] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);
	const [sentTo, setSentTo] = useState(new Map<string, boolean>());
	const [sendingTo, setSendingTo] = useState(new Set<string>());
	const recipients = useRecipientItems();
	const [searchQuery, setSearchQuery] = useState('');
	useEffect(() => {
		let cancelled = false;
		const createShareLink = async () => {
			setLoading(true);
			setThemeUrl(null);
			try {
				const response = await conduit.post<{id: string}>(Endpoints.USER_THEMES, {
					body: {
						css: themeCss,
					},
				});
				const themeId = response.body?.id;
				if (!themeId) {
					throw new Error('Missing theme id');
				}
				if (cancelled) return;
				const origin = RuntimeConfig.webAppBaseUrl;
				setThemeUrl(`${origin.replace(/\/$/, '')}${Routes.theme(themeId)}`);
			} catch (error) {
				logger.error('Failed to create theme share link:', error);
				if (!cancelled) {
					ToastCommands.error(t`Failed to generate theme link.`);
				}
			} finally {
				if (!cancelled) {
					setLoading(false);
				}
			}
		};
		void createShareLink();
		return () => {
			cancelled = true;
		};
	}, [themeCss, RuntimeConfig.webAppBaseUrl]);
	const handleCopy = useCopyLinkHandler(themeUrl, true);
	const handleSendTheme = async (item: RecipientItem) => {
		if (!themeUrl) return;
		const userId = item.type === 'group_dm' ? item.id : item.user.id;
		setSendingTo((prev) => new Set(prev).add(userId));
		let targetChannelId: string;
		if (item.channelId) {
			targetChannelId = item.channelId;
		} else {
			targetChannelId = await PrivateChannelCommands.ensureDMChannel(item.user.id);
		}
		try {
			const result = await MessageCommands.send(targetChannelId, {
				content: `${t`Check out my custom theme!`}\n${themeUrl}`,
				nonce: SnowflakeUtils.fromTimestamp(Date.now()),
			});
			if (result) {
				setSentTo((prev) => new Map(prev).set(userId, true));
			}
		} catch (error) {
			logger.error('Failed to send theme link:', error);
			ToastCommands.error(t`Failed to send theme link. Please try again.`);
		} finally {
			setSendingTo((prev) => {
				const next = new Set(prev);
				next.delete(userId);
				return next;
			});
		}
	};
	return (
		<Modal.Root size="small" centered>
			<Modal.Header title={t`Share Your Theme`}>
				<div className={selectorStyles.headerSearch}>
					<Input
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						placeholder={t`Search friends`}
						leftIcon={<MagnifyingGlassIcon size={20} weight="bold" className={selectorStyles.searchIcon} />}
						className={selectorStyles.headerSearchInput}
					/>
				</div>
			</Modal.Header>
			<Modal.Content className={selectorStyles.selectorContent}>
				{loading ? (
					<div className={styles.loadingContainer}>
						<Spinner />
					</div>
				) : (
					<RecipientList
						recipients={recipients}
						sendingTo={sendingTo}
						sentTo={sentTo}
						onSend={handleSendTheme}
						defaultButtonLabel={t`Send`}
						sentButtonLabel={t`Sent`}
						buttonClassName={styles.sendButton}
						scrollerKey="share-theme-modal-friend-list-scroller"
						searchQuery={searchQuery}
						onSearchQueryChange={setSearchQuery}
						showSearchInput={false}
					/>
				)}
			</Modal.Content>
			<Modal.Footer>
				<CopyLinkSection
					label={<Trans>Or copy the link:</Trans>}
					value={themeUrl || ''}
					onCopy={handleCopy}
					onInputClick={(e) => e.currentTarget.select()}
				/>
			</Modal.Footer>
		</Modal.Root>
	);
});
